import React, { PropsWithChildren, Fragment, memo, useCallback, lazy, Suspense } from 'react';
import Div from '@/shared/Div';
import Heading from '@/shared/Heading';
import classNames from 'classnames';
import Images from '../Image';
import Button from '@/shared/Button';
import Metadata from './Metadata';
import { MapType } from '@/types';
import { getFormattedPrice } from '@/utils/price';
import dlv from 'dlv';
import Price from '@/components/Product/Price';
import { getProductURL } from '@/utils/urls';
import Link from '../Link';
import Paragraph from '../Paragraph';
import Span from '../Span';
import Map from '@/components/Map';
import NCEMI, { INCEMI } from '@/components/Product/NCEMI';
import MetadataTag from './MetadataTag';
import { useAppConfigContext } from '@/context/AppContext';
import Star from '@/components/Icons/Star';
import Share from '@/components/Icons/Share';
import Modal from './Modal';
import useModalToggle from '@/hooks/useModalToggle';
import Commission from '@/components/seller/Commission';

const LazyShare = React.lazy(
  () => import(/* webpackChunkName: "share-link-modal-chunk" */ '@/components/seller/Share'),
);

type Product = {
  materialId?: string;
  id: string;
  url: string;
  plainName?: string;
  media?: any;
  name?: string;
  slug?: string;
  mrp?: number;
  sellingPrice?: number;
  discText?: string;
  image?: any;
  tag?: {
    color?: string;
    text?: string;
  };
  title?: string;
  subTitle?: string;
  description?: string;
  ncemi?: INCEMI;
  dealTag?: any;
  tags: any[];
  featuredTag?: any;
  commission?: number;
};

export interface SkuCardsProps {
  data?: Partial<Product>; // Use the Product type here
  type?: string;
  className?: string;
  dataTestId?: string;
  coupon?: string;
  title?: string;
  price?: string;
  off?: string;
  url?: string;
  ncemi?: INCEMI;
  commission?: number;
  isSCAClient?: boolean;
}

const SkuImage = (props: {
  image?: any;
  className?: string;
  url?: string;
  type?: string;
  useThumbnail?: boolean;
}) => {
  const { image, className, url, useThumbnail = false } = props;

  const img = dlv(image, 'data.0.attributes', dlv(image, 'data.attributes', {}));
  const { url: imgUrl, height, width } = dlv(img, 'formats.thumbnail', img);

  const imgJSX = (
    <Images
      height={useThumbnail ? height : img.height}
      width={useThumbnail ? width : img.width}
      src={useThumbnail ? imgUrl : img.url}
      alt={img.alternativeText}
      className={classNames('w-auto h-auto m-auto', className)}
    />
  );
  if (url) {
    return (
      <Button href={url} theme="none">
        {imgJSX}
      </Button>
    );
  }
  return imgJSX;
};

const SkuTitle = (props: PropsWithChildren<SkuCardsProps>) => {
  const { title, className, url } = props;

  const headingJSX = (
    <Heading
      level={4}
      className={classNames('w-full text-h-2 text-left md:text-hd-2 break-words', className)}
    >
      {title}
    </Heading>
  );

  if (url) {
    return (
      <Button href={url} theme="none">
        {headingJSX}
      </Button>
    );
  }

  return headingJSX;
};

// const SkuPrice = (props: PropsWithChildren<SkuCardsProps>) => {
//   const { className, price } = props;
//   return (
//     <Heading level={2} className={className}>
//       {price}
//     </Heading>
//   );
// };

// const SkuDiscountPrice = (props: PropsWithChildren<SkuCardsProps>) => {
//   const { price, off } = props;
//   return (
//     <Price price={price} discPercent={off} type="trending"/>
//   );
// };

const SkuCouponEMIs = (props: PropsWithChildren<SkuCardsProps>) => {
  const { coupon, ncemi } = props;
  return (
    <Paragraph className="text-body-sm text-extended-efbrand mt-s1">
      <Span className="inline-block">{coupon}</Span>
      <NCEMI className="!text-body-base !text-extended-efbrand mt-s1" data={ncemi || null} />
    </Paragraph>
  );
};

const UpsellMedium = (props: PropsWithChildren<SkuCardsProps>) => {
  const { dataTestId, data = {}, className, isSCAClient } = props;
  const {
    plainName,
    materialId = '',
    slug,
    ncemi,
    dealTag,
    tags = [],
    featuredTag,
    commission,
  } = data || {};

  const deal = dlv(dealTag, 'data.attributes', null);
  const url = getProductURL(materialId, slug, isSCAClient);

  const renderTags = useCallback((tag: any) => <MetadataTag key={tag.label} {...tag} />, []);
  return (
    <Link
      href={url}
      data-testid={dataTestId}
      className={classNames(
        'overflow-hidden flex flex-col rounded-cm border border-cm-subtle text-cm-primary h-full',
        className,
      )}
    >
      <Div className="bg-cs-secondary border-b border-cm-subtle py-8 px-10 pb-0 relative">
        <SkuImage image={data?.media} className="!h-40" useThumbnail />
        {featuredTag ? (
          <MetadataTag
            {...featuredTag}
            className="absolute top-0 left-0 !py-s2 !px-s3 !text-body-xs rounded-none rounded-br-cxxs uppercase"
          />
        ) : null}
        {/* <Metadata
          className="absolute top-0 left-0 !py-s2 !px-s3 !text-body-xs rounded-none rounded-br-cxxs uppercase"
          textColor="text-cs-primary"
          background="bg-purple-800"
          dataTestId=""
        >
          Best seller
        </Metadata> */}
      </Div>
      <Div
        className="grid justify-between gap-s6 p-s5 flex-grow"
        style={{ gridTemplateRows: '1fr 100px' }}
      >
        <Div>
          <SkuTitle className="text-h-4 md:text-hd-4 break-all" title={plainName} />
          <Map
            className="flex items-start h-fit gap-2 mt-s2  flex-wrap"
            list={tags}
            render={renderTags}
          />
        </Div>
        {/* <div className='row-span-'></div> */}
        <Div className="flex flex-col gap-s2 ">
          <Price
            value={{
              sellingPrice: getFormattedPrice(data?.sellingPrice),
              price: getFormattedPrice(data?.mrp),
              discPercent: `(${data?.discText})`,
            }}
            type="trending"
          />
          {deal ? (
            <Metadata
              className="!py-s2 !px-s3 !text-body-xs uppercase w-fit"
              textColor="text-cs-primary"
              background="bg-cu-positive"
              dataTestId=""
              style={{
                background: deal.background,
                color: deal.labelColor,
              }}
            >
              {deal.label}
            </Metadata>
          ) : (
            deal
          )}
          <SkuCouponEMIs coupon="" ncemi={ncemi} />
          <Commission sellingPrice={data?.sellingPrice} commission={commission} />
        </Div>
      </Div>
    </Link>
  );
};

const AccessoriesUpsell = (props: PropsWithChildren<SkuCardsProps>) => {
  const { dataTestId } = props;
  return (
    <Div
      data-testid={dataTestId}
      className="overflow-hidden rounded-cm w-60 border border-cm-subtle text-cm-primary"
    >
      <Div className="bg-cs-secondary border-b border-cm-subtle py-8 px-12 pb-0 relative">
        <SkuImage />
        <Metadata
          className="absolute top-0 left-0 !py-s2 !px-s3 !text-body-xs rounded-none rounded-br-cxxs uppercase"
          textColor="text-cs-primary"
          background="bg-purple-800"
          dataTestId=""
        >
          Best seller
        </Metadata>
      </Div>
      <Div className="p-s5">
        <SkuTitle
          className="text-h-4 md:text-hd-4"
          title="Aquaguard Water Softener Regeneration Media (Salt)"
        />
        <p className="text-body-lg truncate">
          Non-iodised sodium chloride with 99.4% purity. No added preservatives
        </p>
        <Div className="flex items-center justify-between mt-s5 py-2">
          <Heading level={2} className="text-h-3">
            ₹500
          </Heading>
          <Button className="py-s3 px-s4 text-button-small rounded-cxs" dataTestId="addButton">
            Add
          </Button>
        </Div>
      </Div>
    </Div>
  );
};

const UpsellLarge = (props: PropsWithChildren<SkuCardsProps>) => {
  const { dataTestId, data, className } = props;
  const { tag, title, subTitle, description, image, url = '#' } = data ?? {};
  return (
    <Link href={url}>
      <Div
        data-testid={dataTestId}
        className={classNames(
          'relative grid gap-s8 overflow-hidden rounded-cm text-cm-primary h-full',
          className,
        )}
      >
        <Div className="p-s6 pb-0 absolute left-0 right-0 top-0 z-10">
          <p className="text-body-xs uppercase text-cu-positive" style={{ color: `${tag?.color}` }}>
            {tag?.text}
          </p>
          <SkuTitle className="text-h-2 md:text-hd-2 mt-s3" title={title} />
          <p className="text-body-base md:text-body-xl mt-s2">{subTitle}</p>
          <p className="text-h-4 md:text-hd-4 mt-s3">{description}</p>
        </Div>
        <Div className="px-0 pb-0 relative">
          <SkuImage image={image} className="w-full h-full object-cover" />
        </Div>
      </Div>
    </Link>
  );
};

export const PDPCard = (props: PropsWithChildren<SkuCardsProps>) => {
  const {
    open: isShareLinkModalOpen,
    openModal: openShareLinkModal,
    closeModal: closeShareLinkModal,
  } = useModalToggle();
  const { dataTestId, data = {} } = props;
  const { plainName, media, materialId, slug, dealTag, tags, featuredTag, flags, commission } = dlv(
    data,
    'attributes',
    {},
  );
  const price = dlv(data, 'price', {});
  const image = dlv(media, 'data.0.attributes', dlv(media, 'data.attributes', {}));
  const { sellingPrice, mrp, discText, ncemi } = price ?? {};
  const {
    state: { isSCAClient },
  } = useAppConfigContext();
  const href = getProductURL(materialId, slug, isSCAClient);
  const deal = dlv(dealTag, 'data.attributes', null);
  const { commercial } = flags || {};

  const renderTags = useCallback((tag: any) => <MetadataTag key={tag.label} {...tag} />, []);

  const handleShareButtonClick = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    openShareLinkModal();
  };

  return (
    <Fragment>
      <Link
        href={href}
        dataTestId={dataTestId}
        className="flex  md:flex-col overflow-hidden rounded-cm border border-cm-subtle text-cm-primary bg-white"
      >
        <Div className="px-s2 md:py-8 md:px-10 md:pb-0 relative bg-cs-secondary border-r md:border-b md:border-r-0 border-solid  border-cm-subtle w-32 md:w-full flex items-center justify-center">
          <SkuImage image={media} className="md:h-40" useThumbnail />
          {featuredTag ? (
            <MetadataTag
              {...featuredTag}
              className="absolute top-0 left-0 !py-s2 !px-s3 !text-body-xs rounded-none rounded-br-cxxs uppercase"
            />
          ) : null}
        </Div>
        <Div
          className="p-s5 w-[calc(100%-8rem)] md:w-full flex gap-s4 flex-col md:grid  h-full md:gap-s4"
          style={{ gridTemplateRows: '1fr 100px' }}
        >
          <Div className="">
            <Heading
              level={3}
              className="text-h-4 md:text-hd-4 text-cm-primary break-words min-h-fit"
            >
              {plainName}
            </Heading>
            <Map
              className="flex items-start h-fit gap-2 mt-s2  flex-wrap"
              list={tags}
              render={renderTags}
            />
          </Div>
          <Div className="grid gap-s2 h-fit ">
            {commercial ? null : (
              <Price
                value={{
                  sellingPrice: getFormattedPrice(sellingPrice),
                  price: getFormattedPrice(mrp),
                  discPercent: `(${discText})`,
                }}
                type="trending"
              />
            )}
            {deal ? (
              <Metadata
                className="!py-s2 !px-s3 !text-body-xs uppercase w-fit"
                textColor="text-cs-primary"
                background="bg-cu-positive"
                dataTestId=""
                style={{
                  background: deal.background,
                  color: deal.labelColor,
                }}
              >
                {deal.label}
              </Metadata>
            ) : (
              deal
            )}
            {isSCAClient ? (
              <Div className="flex flex-col pt-s3 gap-s3">
                <Commission
                  className="text-blue-400 text"
                  commission={commission}
                  sellingPrice={sellingPrice}
                />

                <Button
                  className="flex gap-s3 z-1 text-button-normal "
                  size="sm"
                  onClick={handleShareButtonClick}
                >
                  <Share />
                  Share Link
                </Button>
              </Div>
            ) : null}
            <SkuCouponEMIs coupon="" ncemi={ncemi} />
          </Div>
        </Div>
      </Link>
      {/* TODO": Remove this modal with SKU Card, share functionality should be Global" */}
      <Modal
        onClose={closeShareLinkModal}
        classNames="md:w-[460px] "
        showCloseButton
        open={isShareLinkModalOpen}
      >
        <Suspense>
          <LazyShare
            priceDetails={{ sellingPrice: price?.mrp, originalPrice: price?.mop }}
            productImage={image?.url}
            productName={plainName}
            commission={commission}
            isPDPPage={false}
            url={href}
          />
        </Suspense>
      </Modal>
    </Fragment>
  );
};

const SKUCARD_COMPONENT_MAP: MapType = {
  UpsellMedium,
  AccessoriesUpsell,
  UpsellLarge,
  PDPCard,
};

const SkuCards = (props: SkuCardsProps) => {
  const { type = 'UpsellMedium', ...restOfProps } = props;
  const Comp = SKUCARD_COMPONENT_MAP[type];
  if (Comp) {
    return <Comp {...restOfProps} />;
  }
  return <Fragment />;
};

export default memo(SkuCards);
